import { useState, useEffect } from 'react'
import { flushSync } from 'react-dom'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'
import { ScrollArea } from '@/components/ui/scroll-area'
import { PdfViewerModal } from './PdfViewerModal'
import { SurgerySourceRow } from './SurgerySourceRow'
import { HiddenSourcesSection } from './HiddenSourcesSection'
import { HiddenPassagesSection, type HiddenPassageEntry } from './HiddenPassagesSection'
import { RemovedCitationsSection } from './RemovedCitationsSection'
import { InlineDiff } from './InlineDiff'
import { AuditTrail } from './AuditTrail'
import { RemoveCitationDialog } from './RemoveCitationDialog'
import type { BoundingBox, Evidence, ProofPassageEntry } from '@/types/api'
import { useReviewPanel } from '@/contexts/ReviewPanelContext'
import { citationLabel } from '@/lib/citationLabel'
import { statusBadgeConfig } from './statusConfig'

function isComplexDecomposition(atomicClaim: string, originalSentence: string): boolean {
  const claimWords = atomicClaim.toLowerCase().split(/\s+/)
  const originalWords = originalSentence.toLowerCase().split(/\s+/)
  // Length ratio: claim is a small fragment of a long original
  if (originalWords.length > 0 && claimWords.length / originalWords.length < 0.6) return true
  // Vocabulary overlap: claim uses very different words than original
  const originalSet = new Set(originalWords)
  const overlap = claimWords.filter(w => originalSet.has(w)).length
  if (claimWords.length > 0 && overlap / claimWords.length < 0.6) return true
  return false
}

export function SurgeryPanel() {
  const {
    apiAuditResult,
    sourceLookup,
    surgeryClaimId,
    setSurgeryClaimId,
    setViewMode,
    hiddenSources,
    hiddenPassages,
    auditLog,
    claimSnapshots,
    removeSourceFromClaim,
    restoreSourceToClaim,
    removePassageFromClaim,
    restorePassageToClaim,
    removeCitation,
    removedCitations,
    restoreCitation,
  } = useReviewPanel()

  // Local UI state
  const [activePdfView, setActivePdfView] = useState<{ sourceId: string; boxes: BoundingBox[] } | null>(null)
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false)
  const [expandedSourceIndex, setExpandedSourceIndex] = useState<number | null>(null)
  // D4: Remove-last → Remove citation dialog state.
  const [removeCitationDialogOpen, setRemoveCitationDialogOpen] = useState(false)
  const [pendingRemoveSourceId, setPendingRemoveSourceId] = useState<string | null>(null)
  // Session-scoped suppression of the Remove-citation confirmation. Resets on reload.
  const [skipRemoveCitationConfirm, setSkipRemoveCitationConfirm] = useState(false)

  // Reset local state when claim changes
  useEffect(() => {
    /* eslint-disable-next-line react-hooks/set-state-in-effect -- intentional reset when parent swaps the surgeried claim */
    setExpandedSourceIndex(null)
  }, [surgeryClaimId])

  // Resolve claim from index
  const claimIndex = surgeryClaimId !== null ? parseInt(surgeryClaimId, 10) : -1
  const entries = apiAuditResult?.atomic_breakdown ?? []
  const entry = claimIndex >= 0 && claimIndex < entries.length ? entries[claimIndex] : null

  if (!entry) {
    return (
      <div className="flex-1 flex items-center justify-center p-6 text-center">
        <p className="text-sm text-muted-foreground">Claim not found.</p>
      </div>
    )
  }

  const claimId = String(claimIndex)
  const snapshot = claimSnapshots.get(claimId)
  const effectiveStatus = snapshot?.effectiveStatus ?? entry.status
  const badge = statusBadgeConfig[effectiveStatus] ?? statusBadgeConfig['unverified']
  const hiddenSet = hiddenSources.get(claimId)
  const hiddenPassageSet = hiddenPassages.get(claimId) ?? new Set<string>()

  // Evidence filtering
  const rawEvidences = (entry.evidences ?? []).filter((ev): ev is Evidence => !!ev?.source_doc)
  const visibleEvidences = hiddenSet
    ? rawEvidences.filter(ev => !hiddenSet.has(ev.source_doc))
    : rawEvidences
  const hiddenEvidences = hiddenSet
    ? rawEvidences.filter(ev => hiddenSet.has(ev.source_doc))
    : []

  // Hidden-passage entries (for the dedicated section). Only surface passages
  // whose parent source is still visible — when a source is itself hidden, its
  // passages are implicitly covered by HiddenSourcesSection above.
  const hiddenPassageEntries: HiddenPassageEntry[] = []
  for (const ev of visibleEvidences) {
    for (const p of ev.proof_passages) {
      if (p.passage_id && hiddenPassageSet.has(p.passage_id)) {
        hiddenPassageEntries.push({ evidence: ev, passage: p })
      }
    }
  }

  function getSourceLabel(sourceId: string): string {
    return citationLabel(sourceLookup.get(sourceId), sourceId)
  }

  function handleBack() {
    setViewMode('triage')
    setSurgeryClaimId(null)
  }

  function handleViewSource(ev: Evidence) {
    // Flatten every visible passage's aligned boxes so the modal highlights
    // all of them at once — useful when the writer wants the holistic view.
    // Per-passage rows have their own jump-to-passage action (handleViewPassage).
    const visibleBoxes = ev.proof_passages
      .filter((p) => !hiddenPassageSet.has(p.passage_id))
      .flatMap((p) => p.boxes)
    flushSync(() => setActivePdfView({ sourceId: ev.source_doc, boxes: visibleBoxes }))
    setPdfViewerOpen(true)
  }

  function handleViewPassage(ev: Evidence, passage: ProofPassageEntry) {
    if (passage.boxes.length === 0) return
    flushSync(() => setActivePdfView({ sourceId: ev.source_doc, boxes: passage.boxes }))
    setPdfViewerOpen(true)
  }

  function handleRemovePassage(ev: Evidence, passage: ProofPassageEntry) {
    if (!passage.passage_id) return
    removePassageFromClaim(claimId, ev.source_doc, passage.passage_id)
    toast.success('Passage removed', {
      action: {
        label: 'Undo',
        onClick: () => restorePassageToClaim(claimId, ev.source_doc, passage.passage_id),
      },
    })
  }

  function handleModalOpenChange(open: boolean) {
    setPdfViewerOpen(open)
    if (!open) setActivePdfView(null)
  }

  function handleRemoveSource(sourceId: string) {
    // D4: if this is the last visible source, the badge would be uncomputable
    // and the citation marker would be empty. Convert to "Remove citation" —
    // a doc-mutating removal of the <sup> node, with confirmation.
    const isLastVisible = visibleEvidences.length === 1 && visibleEvidences[0].source_doc === sourceId
    if (isLastVisible) {
      if (skipRemoveCitationConfirm) {
        removeCitation(claimId, sourceId)
        return
      }
      setPendingRemoveSourceId(sourceId)
      setRemoveCitationDialogOpen(true)
      return
    }
    removeSourceFromClaim(claimId, sourceId)
  }

  function handleConfirmRemoveCitation(suppressFutureDialogs: boolean) {
    if (suppressFutureDialogs) setSkipRemoveCitationConfirm(true)
    if (pendingRemoveSourceId) removeCitation(claimId, pendingRemoveSourceId)
    setRemoveCitationDialogOpen(false)
    setPendingRemoveSourceId(null)
  }

  function handleRestoreSource(sourceId: string) {
    restoreSourceToClaim(claimId, sourceId)
  }

  return (
    <>
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-3 flex flex-col">

          {/* ── 2a. Back button ── */}
          <button
            type="button"
            className="text-[11px] text-primary hover:text-primary/80 cursor-pointer inline-flex items-center gap-1 mb-3 self-start"
            onClick={handleBack}
          >
            <ArrowLeft className="h-3 w-3" />
            Back to all claims
          </button>

          {/* ── 2b. Claim title ── */}
          <p className="text-[13px] font-medium text-foreground leading-[1.4] mb-2">
            {entry.claim}
          </p>

          {/* ── 2c. Badge row ── */}
          <div className="flex items-center gap-1.5 flex-wrap mb-3.5">
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-[10px] ${badge.className}`}>
              {badge.label}
            </span>
            {entry.scrutiny_flags?.map(flag => (
              <span
                key={flag.type}
                className="text-[10px] px-2 py-0.5 rounded-[10px] border border-border text-muted-foreground"
              >
                {flag.type.charAt(0).toUpperCase() + flag.type.slice(1)} claim
              </span>
            ))}
          </div>

          {/* ── 2d. Sources section ── */}
          <div className="mb-3.5">
            <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
              Sources ({visibleEvidences.length})
            </div>
            {visibleEvidences.map((ev, i) => (
              <SurgerySourceRow
                key={ev.source_doc + i}
                evidence={ev}
                sourceLabel={getSourceLabel(ev.source_doc)}
                isExpanded={expandedSourceIndex === i}
                hiddenPassageIds={hiddenPassageSet}
                onToggle={() => setExpandedSourceIndex(expandedSourceIndex === i ? null : i)}
                onViewSource={() => handleViewSource(ev)}
                onRemoveSource={() => handleRemoveSource(ev.source_doc)}
                onViewPassage={(p) => handleViewPassage(ev, p)}
                onRemovePassage={(p) => handleRemovePassage(ev, p)}
              />
            ))}
            {visibleEvidences.length === 0 && (
              <p className="text-[11px] text-muted-foreground">No evidence available for this claim.</p>
            )}
          </div>

          {/* ── 2e. Hidden sources ── */}
          <HiddenSourcesSection
            hiddenEvidences={hiddenEvidences}
            getSourceLabel={getSourceLabel}
            onRestore={handleRestoreSource}
          />

          {/* ── 2e′. Hidden passages (per-passage curation) ── */}
          <HiddenPassagesSection
            entries={hiddenPassageEntries}
            getSourceLabel={getSourceLabel}
            onRestore={(sourceId, passageId) => restorePassageToClaim(claimId, sourceId, passageId)}
          />

          {/* ── 2e'. Removed citations (session-scoped) ── */}
          <RemovedCitationsSection
            removedCitations={removedCitations}
            getClaimLabel={(claimUuid) => {
              for (const [, snap] of claimSnapshots) {
                if (snap.claimUuid === claimUuid) {
                  const idx = parseInt(snap.claimId, 10)
                  return entries[idx]?.claim ?? claimUuid
                }
              }
              return claimUuid
            }}
            getSourceLabel={getSourceLabel}
            onRestore={restoreCitation}
          />

          {/* ── 2f. Force a source — hidden until backend lands.
                Future direction: re-search the verifier against a single
                user-picked source so retrieval misses can be recovered.
                Prior placeholder UI removed in commit history if needed. ── */}

          {/* ── 2g. Divider ── */}
          <div className="h-px bg-border/40 my-3" />

          {/* ── 2h. Suggested fix ── */}
          <div className="mb-3.5">
            <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
              Suggested fix (in context)
            </div>
            {snapshot?.suggestedFixCleared ? (
              effectiveStatus === 'verified' ? (
                <p className="text-[11px] text-emerald-600">No fix needed — claim is fully supported.</p>
              ) : effectiveStatus === 'unverified' ? (
                <p className="text-[11px] text-amber-600">Original fix no longer applies — no active sources remain.</p>
              ) : (
                <p className="text-[11px] text-amber-600">Original fix no longer applies — excluded source changed the evidence context.</p>
              )
            ) : entry.suggested_edit ? (
              entry.original_sentence && isComplexDecomposition(entry.claim, entry.original_sentence) ? (
                <>
                  <p className="text-[11px] text-amber-600 italic mb-1.5">
                    This claim spans a complex sentence. The suggested fix applies to the decomposed claim — review and edit the original text directly.
                  </p>
                  <div className="bg-muted rounded-lg p-2 px-2.5 text-[11px] leading-relaxed text-foreground">
                    {entry.suggested_edit}
                  </div>
                </>
              ) : (
                <InlineDiff originalText={entry.claim} suggestedText={entry.suggested_edit} />
              )
            ) : (
              <p className="text-[11px] text-muted-foreground">No revision suggested for this claim.</p>
            )}
          </div>

          {/* ── 2i. AI analysis ── */}
          <div className="mb-3.5">
            <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
              AI analysis
            </div>
            <div className="bg-muted rounded-lg p-2 px-2.5 text-[11px] leading-relaxed text-muted-foreground">
              {snapshot?.effectiveAnalysis || entry.reasoning || 'No AI analysis available for this claim.'}
            </div>
          </div>

          {/* ── Audit trail ── */}
          <div className="h-px bg-border/40 my-3" />
          <AuditTrail entries={auditLog.get(claimId) ?? []} />

          <div className="pb-4" />
        </div>
      </ScrollArea>

      {/* PDF Viewer Modal */}
      {activePdfView && (
        <PdfViewerModal
          open={pdfViewerOpen}
          onOpenChange={handleModalOpenChange}
          documentId={activePdfView.sourceId}
          boxes={activePdfView.boxes}
        />
      )}

      {/* D4: Remove-last → Remove citation confirmation */}
      <RemoveCitationDialog
        open={removeCitationDialogOpen}
        onOpenChange={(open) => {
          setRemoveCitationDialogOpen(open)
          if (!open) setPendingRemoveSourceId(null)
        }}
        onConfirm={handleConfirmRemoveCitation}
      />
    </>
  )
}
